import { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, ChevronRight } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { HERO_BLUE } from "@/lib/marketing";
import { HowItWorksButton } from "@/components/walkthrough/HowItWorksButton";
import { navKey, type MarketingNavLink } from "@/lib/marketingNav";

/**
 * Marketing nav for widths below md, where the inline links don't fit.
 *
 * Before this, the marketing links simply vanished under md with no fallback —
 * a phone visitor could not reach Features, Pricing or FAQ from the header at
 * all. This is that missing affordance, not a restyle.
 *
 * Built on the shared Sheet (Radix Dialog), so focus trap, Escape, overlay
 * click, scroll lock and the close button all come from the component we
 * already ship rather than being hand-rolled.
 *
 * DEFAULTS, all easy to change in a redesign: right-hand drawer, standard
 * hamburger glyph at the end of the header row, every marketing link plus Log in
 * and the trial CTA, closing on tap / overlay / Escape. The CTA label mirrors the
 * header button on purpose — the drawer is that row's below-md stand-in, so the
 * two must not drift.
 */

export default function MarketingMobileNav({
  links,
  renderSectionLink,
}: {
  links: MarketingNavLink[];
  /** Reuses MarketingLayout's SectionLink so drawer links resolve cross-page. */
  renderSectionLink: (args: {
    hash: string;
    className: string;
    onNavigate: () => void;
    children: React.ReactNode;
  }) => React.ReactNode;
}) {
  const [open, setOpen] = useState(false);

  // Same-page anchors fire while the dialog still owns the scroll lock, and
  // Radix restores the pre-open scroll position on close — which would undo the
  // jump. Closing first and re-asserting the target after the close settles
  // keeps the landing behaviour identical to tapping the desktop nav.
  const close = () => setOpen(false);
  const closeThenLand = (hash: string) => () => {
    setOpen(false);
    const id = hash.replace(/^\//, "").slice(1);
    window.setTimeout(() => {
      document.getElementById(id)?.scrollIntoView();
    }, 220);
  };

  const itemClass =
    "flex items-center justify-between rounded-lg px-3 py-3 text-[17px] font-medium text-neutral-800 transition hover:bg-neutral-100";

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          aria-label="Open menu"
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-neutral-700 transition hover:bg-neutral-100 lg:hidden"
        >
          <Menu className="h-6 w-6" strokeWidth={2} />
        </button>
      </SheetTrigger>

      <SheetContent side="right" className="w-[292px] bg-white p-0 sm:max-w-[292px]">
        <SheetHeader className="border-b border-neutral-200 px-5 py-4">
          <SheetTitle className="text-left text-[15px] font-bold text-neutral-900">Menu</SheetTitle>
        </SheetHeader>

        <nav aria-label="Marketing" className="flex flex-col gap-1 px-2 py-3">
          {links.map((link) =>
            link.kind === "action" ? (
              <HowItWorksButton
                key={navKey(link)}
                className={`${itemClass} w-full text-left`}
                onOpen={close}
              >
                {link.label}
                <ChevronRight className="h-4 w-4 text-neutral-400" aria-hidden />
              </HowItWorksButton>
            ) : link.kind === "route" ? (
              <Link key={navKey(link)} to={link.to} className={itemClass} onClick={close}>
                {link.label}
                <ChevronRight className="h-4 w-4 text-neutral-400" aria-hidden />
              </Link>
            ) : (
              <span key={navKey(link)}>
                {renderSectionLink({
                  hash: link.hash,
                  className: itemClass,
                  onNavigate: closeThenLand(link.hash),
                  children: (
                    <>
                      {link.label}
                      <ChevronRight className="h-4 w-4 text-neutral-400" aria-hidden />
                    </>
                  ),
                })}
              </span>
            ),
          )}
        </nav>

        <div className="mt-auto border-t border-neutral-200 px-5 py-4">
          <Link
            to="/auth/login"
            onClick={close}
            className="flex w-full items-center justify-center rounded-lg border border-neutral-300 px-4 py-2.5 text-[15px] font-semibold text-neutral-800"
          >
            Log in
          </Link>
          <Link
            to="/auth/signup"
            onClick={close}
            className="mt-3 flex w-full items-center justify-center gap-1 rounded-lg px-4 py-2.5 text-[15px] font-semibold text-white shadow-sm"
            style={{ backgroundColor: HERO_BLUE }}
          >
            Start 7-Day Free Trial <ChevronRight className="h-4 w-4" strokeWidth={2.5} />
          </Link>
        </div>
      </SheetContent>
    </Sheet>
  );
}
